import dayjs from 'dayjs';

export const validateZip = {
  value: /^\d{5}(-\d{4})?$/,
  message: 'Enter a valid US zip code (e.g. 12345 or 12345-6789)',
};

export const validateNPI = (value) => {
  if (value?.length === 0 || value === null) return true;

  const isTenDigits = /^\d{10}$/.test(value);
  if (!isTenDigits) return 'NPI number must be 10 digits';

  //Luhn NPI Number Checksum
  const prefix = '80840';
  const digits = (prefix + value?.slice(0, 9))?.split('')?.map(Number).reverse();

  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    let digit = digits[i];
    if (i % 2 === 0) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }

  const checkDigit = (10 - (sum % 10)) % 10;
  if (checkDigit !== Number(value[9])) return 'Invalid NPI number';

  return true;
};

export const validateEIN = (value) => {
  const digits = value?.replace(/\D/g, '') || '';
  if (digits.length === 0) return true;
  return digits.length === 9 || 'Enter a valid 9-digit EIN';
};

export const validateDate = (value, isRequired, minDate = null, maxDate = null) => {
  if (!value && !isRequired) return true;

  try {
    const date = dayjs(value).startOf('day').toDate();

    if (Number.isNaN(date.getTime())) {
      return 'Please enter a valid date';
    }

    if (minDate) {
      if (date < minDate) {
        return `Date must be on or after ${minDate.toLocaleDateString()}`;
      }
    }

    if (maxDate) {
      if (date > maxDate) {
        return `Date must be on or before ${maxDate.toLocaleDateString()}`;
      }
    }

    return true;
  } catch {
    return 'Please enter a valid date';
  }
};

export const validateDEA = (value) => {
  if (!value) return true;

  const isCorrectFormat = /^[A-Z]{2}\d{7}$/.test(value);
  if (!isCorrectFormat) return 'DEA number must be 2 uppercase letters followed by 7 digits';

  //DEA Number Checksum
  const digits = value?.slice(2)?.split('')?.map(Number);
  const checkDigit = digits[6];

  const sum1 = digits[0] + digits[2] + digits[4];
  const sum2 = (digits[1] + digits[3] + digits[5]) * 2;
  const total = sum1 + sum2;

  if (total % 10 !== checkDigit) return 'Invalid DEA number';

  return true;
};

export const validatePhoneNumber = (value) => {
  const digits = value?.replace(/\D/g, '') || '';
  if (digits.length === 0) return true;
  return digits.length === 10 || 'Enter a valid 10-digit phone';
};

export const validateRoutingNumber = (value) => {
  if (value?.length === 0 || value === null) return true;

  const isNineDigits = /^\d{9}$/.test(value);
  if (!isNineDigits) return 'Routing number must be 9 digits';

  // ABA Routing Number Checksum
  const digits = value.split('').map(Number);
  const checksum =
    3 * (digits[0] + digits[3] + digits[6]) +
    7 * (digits[1] + digits[4] + digits[7]) +
    1 * (digits[2] + digits[5] + digits[8]);
  if (checksum % 10 !== 0) return 'Invalid routing number';

  return true;
};

export const validateMedicare = (value) => {
  if (value?.length === 0 || value === null) return true;
  return value?.length === 6 || 'Enter a valid Medicare number';
};

export const validateEmail = {
  value: /^[A-Za-z0-9._%+-]{0,}@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\s*$/,
  message: 'Enter a valid email address (e.g. name@domain.com)',
};

export const validateWebsiteDomain = {
  value: /^\s*(?:www\.)?(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}\s*$/,
  message: 'Enter a valid domain (e.g. example.com)',
};

export const validatePositiveNumber = {
  value: /^(0|[1-9]\d*)(\.\d+)?$/,
  message: 'Must be a positive whole number or zero',
};

export const validateAddress = (value) => {
  if (!value || value.length === 0) return true;
  if (value.length < 5) return 'Address must be at least 5 characters';
  if (!/[A-Za-z0-9]/.test(value)) return 'Address must contain at least one letter or number';
  return true;
};

export const validateRequiredNameWhenHasFiles = (value, hasActiveFiles) => {
  if (!hasActiveFiles) return true;
  return `${value ?? ''}`.trim().length > 0 || 'Name is required when files are attached';
};

export const validateVIN = (value) => {
  if (!value) return true;

  const vin = value.toUpperCase();

  const isCorrectFormat = /^[A-HJ-NPR-Z0-9]{17}$/.test(vin);
  if (!isCorrectFormat)
    return 'VIN must be exactly 17 characters and may contain only letters and numbers, excluding I, O, and Q.';

  const transliteration = {
    A: 1,
    B: 2,
    C: 3,
    D: 4,
    E: 5,
    F: 6,
    G: 7,
    H: 8,
    J: 1,
    K: 2,
    L: 3,
    M: 4,
    N: 5,
    P: 7,
    R: 9,
    S: 2,
    T: 3,
    U: 4,
    V: 5,
    W: 6,
    X: 7,
    Y: 8,
    Z: 9,
  };

  const weights = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];

  let sum = 0;

  for (let i = 0; i < 17; i++) {
    const character = vin[i];

    const value = /\d/.test(character) ? Number(character) : transliteration[character];

    if (value === undefined) {
      return false;
    }

    sum += value * weights[i];
  }

  const remainder = sum % 11;

  const expectedCheckDigit = remainder === 10 ? 'X' : remainder.toString();

  if (vin[8] !== expectedCheckDigit) return 'Invalid VIN number';

  return true;
};
